import { ParticipantsState } from "../Redux/reducers/participantsSlice";
import { isCustomFilterValueActive } from "./participantDrawerFilters";

export type { ParticipantsState };

const STUDY_COLUMN_FILTER_KEYS: Record<string, string | string[]> = {
  site: "siteIds",
  status: "status",
  screenerCompleted: "screenerCompleted",
  screenerReceived: "screenerReceived",
  screenerCompletedDate: ["screenerCompletedFrom", "screenerCompletedTo"],
  userGroup: "cohortId",
  tags: "tagIds",
  lock: "isLocked",
  actions: "notesQuery",
};

export function isStudyColumnFilterActive(
  columnKey: string,
  state: Pick<
    ParticipantsState,
    | "siteIds"
    | "status"
    | "isLocked"
    | "screenerCompleted"
    | "screenerReceived"
    | "screenerCompletedFrom"
    | "screenerCompletedTo"
    | "cohortId"
    | "tagIds"
    | "notesQuery"
    | "customHeaderValues"
  >,
  /** Required for custom columns — columnKey may use questionId, filters use varname */
  customVarname?: string,
): boolean {
  if (columnKey.startsWith("custom:")) {
    const lookupKey = customVarname ?? columnKey.replace("custom:", "");
    return state.customHeaderValues.some(
      (v) => v.key === lookupKey && isCustomFilterValueActive(v.value),
    );
  }

  const mapped = STUDY_COLUMN_FILTER_KEYS[columnKey];
  if (!mapped) return false;

  const keys = Array.isArray(mapped) ? mapped : [mapped];
  return keys.some((key) => {
    const val = state[key as keyof typeof state];
    if (Array.isArray(val)) return val.length > 0;
    return !!val;
  });
}

const REGISTRY_COLUMN_FILTER_KEYS: Record<string, string | string[]> = {
  systemId: "participantCode",
  status: "status",
  redactionStatus: "redactionStatus",
  screenerCompletedDate: ["screenerCompletedFrom", "screenerCompletedTo"],
  notes: "notesQuery",
  tags: "tagIds",
};

export function isRegistryColumnFilterActive(
  columnKey: string,
  filters: Record<string, string>,
): boolean {
  const mapped = REGISTRY_COLUMN_FILTER_KEYS[columnKey];
  if (!mapped) return false;

  const keys = Array.isArray(mapped) ? mapped : [mapped];
  return keys.some((key) => !!filters[key]);
}

export function hasStudyListFilters(
  state: Pick<
    ParticipantsState,
    | "siteIds"
    | "status"
    | "isLocked"
    | "cohortId"
    | "tagIds"
    | "screenerCompleted"
    | "screenerReceived"
    | "screenerCompletedFrom"
    | "screenerCompletedTo"
    | "notesQuery"
    | "customHeaderValues"
    | "searchQuery"
  >,
): boolean {
  return (
    state.siteIds.length > 0 ||
    state.status.length > 0 ||
    !!state.isLocked ||
    state.cohortId.length > 0 ||
    state.tagIds.length > 0 ||
    !!state.screenerCompleted ||
    !!state.screenerReceived ||
    !!state.screenerCompletedFrom ||
    !!state.screenerCompletedTo ||
    !!state.notesQuery ||
    state.customHeaderValues.some((v) => isCustomFilterValueActive(v.value)) ||
    !!state.searchQuery
  );
}

export function hasRegistryListFilters(
  filters: Record<string, string>,
  currentSearchId?: string,
): boolean {
  const hasFilter = Object.values(filters).some((v) => v !== "" && v != null);
  return hasFilter || !!currentSearchId;
}
